import { useQuery } from "@tanstack/react-query";
import { getListTeachers } from "api/teacher.api.ts";
import { mapStyle } from "conts/mapbox";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { userRoute } from "pages/user/user.routes";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { MarkerClassCard } from "ui/base/class/MarkerClassCard";
import { fonts, mediaSizes, defaultThemeColors } from "ui/theme";
import { getAvatarUrl, getLowestValidPrice } from "utils";
import { debounce, throttle } from "utils/func";

const DEFAULT_LOCATION = { latitude: 40.7127753, longitude: -74.0059728 };
const QUERY = "Music";

export const HomeMapSection = () => {
    const navigate = useNavigate();
    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerRefs = useRef([]);

    const [zoom, setZoom] = useState(0);
    const [mapError, setMapError] = useState(null);
    const [mapCenter, setMapCenter] = useState(DEFAULT_LOCATION);

    const { data } = useQuery({
        queryKey: ["getListTeachers", QUERY, mapCenter],
        queryFn: () => getListTeachers({ query: QUERY, ...mapCenter }),
    });

    const classes = data?.data || [];

    useEffect(() => {
        if (mapInstanceRef.current) return;

        try {
            const token = process.env.REACT_APP_MAPBOX_TOKEN;
            if (!token) {
                throw new Error("Mapbox token missing!");
            }

            mapboxgl.accessToken = token;

            const map = new mapboxgl.Map({
                container: mapContainerRef.current,
                style: mapStyle,
                center: [DEFAULT_LOCATION.longitude, DEFAULT_LOCATION.latitude],
                zoom: 11,
            });

            map.addControl(new mapboxgl.NavigationControl(), "top-right");

            const updateMapCenter = debounce(() => {
                const center = map.getCenter();
                setMapCenter({
                    latitude: center.lat,
                    longitude: center.lng,
                });
            }, 500);

            map.on("move", updateMapCenter);

            const updateZoom = throttle(() => {
                setZoom(map.getZoom());
            }, 500);
            map.on("zoom", updateZoom);

            mapInstanceRef.current = map;

            return () => {
                updateMapCenter.cancel && updateMapCenter.cancel();
                map.remove();
            };
        } catch (error) {
            console.error("Map initialization error:", error);
            setMapError(error.message);
        }
    }, []);

    useEffect(() => {
        const mapInstance = mapInstanceRef.current;
        if (!mapInstance || !classes.length) return;

        markerRefs.current?.forEach((marker) => marker.remove());
        markerRefs.current = [];

        const seenPositions = new Set();

        classes.forEach((item, index) => {
            const positionKey = `${item.lng}-${item.lat}`;
            let [lng, lat] = [+item.lng, +item.lat];

            if (seenPositions.has(positionKey)) {
                const maxZoom = 25.5;
                const minOffset = 0.00000001;
                const maxOffset = 0.002;

                const zoomFactor =
                    minOffset + (maxOffset - minOffset) * Math.pow(1 - zoom / maxZoom, 3);

                const angle = index * 137.5 * (Math.PI / 180);

                const indexFactor = Math.pow(index, 0.6);

                lng += Math.cos(angle) * zoomFactor * indexFactor;
                lat += Math.sin(angle) * zoomFactor * indexFactor;
            }

            seenPositions.add(positionKey);

            const markerElement = document.createElement("div");
            markerElement.innerHTML = MarkerClassCard({
                zoomLevel: zoom,
                classData: {
                    img: getAvatarUrl(item.profile_pic || ""),
                    rate: item.rate || 0,
                    review: item.review || 0,
                    title: item.subject_sub_category,
                    price: getLowestValidPrice(
                        item?.type_of_class,
                        item?.price_per_group_class,
                        item?.price_per_private_class
                    )
                },
            });

            markerElement.addEventListener("click", () => {
                if (zoom > 12) {
                    navigate(userRoute.classDetail + item.user_id);
                    return;
                }
                mapInstance.flyTo({
                    center: [lng, lat],
                    zoom: 16,
                    essential: true,
                });
            });

            const marker = new mapboxgl.Marker({ element: markerElement })
                .setLngLat([lng, lat])
                .addTo(mapInstance);

            markerRefs.current.push(marker);
        });

        return () => {
            markerRefs.current?.forEach((marker) => marker.remove());
        };
    }, [classes, zoom, navigate]);

    if (mapError) return <div>{mapError}</div>;

    return (
        <ShowMapContainer>
            <TitleContainer>
                <Title>See Classes and Instructors Near&nbsp;You</Title>
            </TitleContainer>
            <MapContainer>
                <MapContent ref={mapContainerRef} />
            </MapContainer>
        </ShowMapContainer>
    );
};

const ShowMapContainer = styled.div`
    border-radius: 5px;
    width: 100%;
    display: grid;
    gap: 48px;    
`;

const TitleContainer = styled.div`
    display: grid;
    gap: 1.2rem;
    width: 100%;
    overflow-wrap: anywhere;
    padding-top: 50px;
`;

const Title = styled.div`
    font-size: 32px;
    font-weight: 500;
    color: ${defaultThemeColors.basicBlack};
    line-height: 1.2;
    text-align: center;

    @media (max-width: ${mediaSizes.lg}) {
        font-size: 28px;
    }

    @media (max-width: ${mediaSizes.md}) {
        font-size: 26px;
    }

    @media (max-width: ${mediaSizes.xs}) {
        font-size: 22px;
        margin-top: 24px;
    }
`;

const MapContainer = styled.div`
    padding-bottom: 1rem;
    width: 100%;
    height: 494px;
`;

const MapContent = styled.div`
    height: 100%;
    width: 100%;
    border-radius: 20px;

    .mapboxgl-map {
        border-radius: 20px;
    }

    .mapboxgl-ctrl-top-right {
        margin-top: 10px;
        margin-right: 10px;
    }

    .mapboxgl-canvas-container {
        cursor: grab;

        &:active {
            cursor: grabbing;
        }
    }
`;
